﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { Entities, ModalMessageType } from '../models/enums';
import { AnalyticalPlan } from '../models/analytical-plan';
import { LookupData } from '../models/lookup-data';
import { Method } from '../models/Methods/method';
import AnalyticalPlanEditComponent from './AnalyticalPlans/AnalyticalPlanEditComponent';
import ReplicatesComponent from './AnalyticalPlans/ReplicatesComponent';
import MethodLookupComponent from './Methods/MethodLookupComponent';
import AuditHistoryComponent from './AuditHistoryComponent';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import { ModalMetadata } from '../models/modal-metadata';
import GlobalDialog from './Modal/GlobalDialog';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Toolbar,
    Editing,
    Item as ToolbarItem,
    Button as ColumnButton
} from 'devextreme-react/data-grid';

import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import Toast from 'devextreme-react/toast';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export interface Props {
    productId: string;
    isReadOnly: boolean;
    lookupData: LookupData;
    updated?: number;
}

export default function ProductAnalyticalPlansData(props: Props) {
    const [analyticalPlanTechnologies, setAnalyticalPlanTechnologies] =
        useState<AnalyticalPlan[]>();

    const [selectedAnalyticalPlan, setSelectedAnalyticalPlan] =
        useState<AnalyticalPlan>();

    const [selectedReplicateStandardId, setSelectedReplicateStandardId] =
        useState<string>();

    const [auditEntity, setAuditEntity] =
        useState<Entities>();

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [addTechnologyDialogVisible, setAddTechnologyDialogVisible] =
        useState<boolean>();

    const [addTechnologySelection, setAddTechnologySelection] =
        useState<number>();

    const [editAnalyticalPlanTechnologyDialogVisible, setEditAnalyticalPlanTechnologyDialogVisible] =
        useState<boolean>();

    const [methodLookupComponentVisible, setMethodLookupComponentVisible] =
        useState<boolean>();

    const [replicatesEditingComponentVisible, setReplicatesEditingComponentVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.productId]);

    useEffect(() => {
        getAllData();
    }, [props.updated]);

    ++key;

    return (
        <div className="pw-margin-top-05">

            <div>
                <DataGrid
                    height='16rem'
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    keyExpr="analyticalPlanId"
                    columnResizingMode="nextColumn"
                    dataSource={analyticalPlanTechnologies ? analyticalPlanTechnologies : []}
                    onSelectionChanged={onAnalyticalPlansSelectionChanged}
                    onRowRemoving={onAnalyticalPlansRowRemoving}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <Selection mode='single' />
                    <Paging enabled={false} />

                    <Column type="buttons" width='8rem' >
                        <ColumnButton hint="edit analytical plan" icon="edit"
                            onClick={showEditAnalyticalPlanTechnologyDialog} />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onAnalyticalPlansAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onAnalyticalPlansInformationButtonClicked} />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column dataField='technologyName' caption="Technology" width="8rem" dataType="string" />
                    <Column dataField='scopesTypeNamesList' caption="Scope Types" dataType="string" />
                    <Column dataField='methodDescription' width="20rem" dataType="string" />
                   
                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Analytical Plan Technologies</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button type='success' width='1.8rem' icon='clock'
                                text=''
                                hint='Analytical Plan Audit Information'
                                onClick={showAnalyticalPlansAuditInformationDialog} />
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <Button type='normal' width='1.8rem' icon='plus'
                                text=''
                                hint='Add technology to Analytical Plan'
                                onClick={addAnalyticalPlanTechnology} />
                        </ToolbarItem>
                    </Toolbar>

                    <Editing
                        mode="row"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={false}
                        allowDeleting={true} >
                    </Editing>

                </DataGrid>
            </div>

            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Replicates</div>

            {(!selectedAnalyticalPlan || !selectedAnalyticalPlan.standards || selectedAnalyticalPlan.standards.length < 1) &&
                <div className="pw-readonly-label">No replicated defined</div>
            }

            {selectedAnalyticalPlan && selectedAnalyticalPlan.standards && selectedAnalyticalPlan.standards.map((s, i) =>
                <div key={i} className="pw-margin-top-05">
                    <div className="pw-form-unit">
                        <div className="pw-readonly-label">{`${s.manufacturerName} - ${s.productName} (${s.catalogNumber})`}</div>
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Sample Replicates</div>
                            <div className="pw-readonly-label">{s.sampleReplicatesString}</div>
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Standard Replicates</div>
                            <div className="pw-readonly-label">{s.standardReplicatesString}</div>
                        </div>

                        <div className="pw-row-button-08">
                            <Button id="editReplicatesButton" data-id={s.standardId} text="" icon="edit" type="success"
                                className="pw-button"
                                hint="edit sample replicates"
                                height='1.6rem'
                                onClick={() => onEditSampleReplicatesClick(s.standardId)} />
                        </div>
                    </div>
                </div>
            )
            }

            {editAnalyticalPlanTechnologyDialogVisible &&
                <DialogPopup visible={editAnalyticalPlanTechnologyDialogVisible}
                    onHiding={hideEditAnalyticalPlanTechnologyDialog}
                    title="Edit Analytical Plan Technology"
                    fullScreen={true}>
                    <AnalyticalPlanEditComponent analyticalPlanId={selectedAnalyticalPlan &&
                        selectedAnalyticalPlan.analyticalPlanId ? selectedAnalyticalPlan.analyticalPlanId : undefined}
                        isReadOnly={props.isReadOnly} lookupData={props.lookupData} onClose={hideEditAnalyticalPlanTechnologyDialog} />
                </DialogPopup>
            }

            {addTechnologyDialogVisible &&
                <DialogPopup visible={addTechnologyDialogVisible}
                    onHiding={hideAddTechnologyDialog}
                    title="Add Analytical Plan Technology"
                    width='30rem'
                    height='20rem'>
                    <div>
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Technology</div>
                            <SelectBox id="addTechnologyTechnologyTypesSelectBox" className="selectbox"
                                placeholder='NA'
                                displayExpr='name' valueExpr='id'
                                dataSource={props.lookupData && props.lookupData.technologyTypes ?
                                    props.lookupData.technologyTypes : []}
                                onValueChanged={onAddTechnologySelectionChanged}
                                value={addTechnologySelection ? addTechnologySelection : undefined} />
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-centered-buttons-container">
                                <Button id="applySelectionButton"
                                    className="pw-button"
                                    text="Apply" type="success"
                                    hint="apply selection"
                                    height='2.6em'
                                    onClick={onApplyAddedTechnologyClick} />

                                <Button id="cancelButton"
                                    className="pw-button"
                                    text="Cancel" type="normal"
                                    hint="cancel"
                                    height='2.6em'
                                    onClick={hideAddTechnologyDialog} />
                            </div>
                        </div>
                    </div>

                </DialogPopup>
            }

            {replicatesEditingComponentVisible &&
                <DialogPopup visible={replicatesEditingComponentVisible}
                    onHiding={hideReplicatesEditingComponent}
                    title="Replicates"
                    fullScreen={true}>
                    <ReplicatesComponent analyticalPlan={selectedAnalyticalPlan ? selectedAnalyticalPlan : null}
                        isReadOnly={props.isReadOnly} standardId={selectedReplicateStandardId} onClose={onReplicateComponentClose} />
                </DialogPopup>
            }

            {methodLookupComponentVisible &&
                <DialogPopup visible={methodLookupComponentVisible}
                    onHiding={hideMethodLookupComponent}
                    title="Method Lookup"
                    minWidth='80vw'
                    minHeight='80vh'
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <MethodLookupComponent showTitle={true}
                        onApplySelection={onApplyMethodApplySelectionClick}
                        onCancel={onHideMethodLookupClick} />
                </DialogPopup>
            }

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={auditEntity}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onReplicateComponentClose() {
        getProductAnalyticalPlanTechnologies();
        hideReplicatesEditingComponent();
    }

    function onEditSampleReplicatesClick(standardId: string) {
       setSelectedReplicateStandardId(standardId);
               showReplicatesEditingComponent();
    }

    function onHideMethodLookupClick() {
        setMethodLookupComponentVisible(false);
    }

    async function onApplyMethodApplySelectionClick(method: Method) {
        setMethodLookupComponentVisible(false);
    }

    function onAnalyticalPlansSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) 
            setSelectedAnalyticalPlan(e.selectedRowsData[0]);
            }

    function addAnalyticalPlanTechnology() {
        setAddTechnologyDialogVisible(true);
    }

    function onAddTechnologySelectionChanged(e: any) {
        setAddTechnologySelection(e.value);
    }

    async function onApplyAddedTechnologyClick() {
        if (!addTechnologySelection)
            return;

        const newAnalyticalPlan: AnalyticalPlan = {
            productId: props.productId,
            analyticalTechnologyId: addTechnologySelection
        }

        await insertAnalyticalPlan(newAnalyticalPlan)
            .then(() => {
                showToast("Analytical Plan Technology Added Successfully", "success");
                getProductAnalyticalPlanTechnologies();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Adding Analytical Plan technology");
            });

        setAddTechnologyDialogVisible(false);
    }

    async function onAnalyticalPlansRowRemoving(e: any) {
        if (e.data && e.data.analyticalPlanId) {
            deleteAnalyticalPlan(e.data.analyticalPlanId)
                .then(data => {
                    showToast("Analytical Plan Technology Deleted", "success");
                    getProductAnalyticalPlanTechnologies();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Analytical Plan");
                });
        }
    }

    async function getProductAnalyticalPlanTechnologies() {
        await CrudFunctions.getData(`analyticalplans/productanalyticalplantechnologies/${props.productId}`)
            .then(data => {
                console.log("AP Technos");
                console.log(data);

                setAnalyticalPlanTechnologies(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Plan Technologies");
            });
    }

    //POST functions

    async function insertAnalyticalPlan(analyticalPlan: AnalyticalPlan) {
        return CrudFunctions.postData("analyticalplans/analyticalplan", analyticalPlan);
    }

    // DELETE functions

    async function deleteAnalyticalPlan(analyticalPlanId: string) {
        return CrudFunctions.deleteData(`analyticalplans/analyticalplan/${analyticalPlanId}`);
    }

    function onAnalyticalPlansAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setAuditEntity(Entities.AnalyticalPlans);
            setAuditRowPointer(e.row.data.analyticalPlanId);
            setAuditInformationDialogVisible(true);
        }
    }

    function onAnalyticalPlansInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.analyticalPlanId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function showEditAnalyticalPlanTechnologyDialog(e: any) {
        setSelectedAnalyticalPlan(e.row.data);

        setEditAnalyticalPlanTechnologyDialogVisible(true);
    }

    function hideEditAnalyticalPlanTechnologyDialog() {
        setEditAnalyticalPlanTechnologyDialogVisible(false);
        getProductAnalyticalPlanTechnologies();
    }

    function showReplicatesEditingComponent() {
        setReplicatesEditingComponentVisible(true);
    }

    function hideReplicatesEditingComponent() {
        setReplicatesEditingComponentVisible(false);
        getProductAnalyticalPlanTechnologies();
    }

    function showAnalyticalPlansAuditInformationDialog() {
        showAuditInformationDialog(Entities.AnalyticalPlans);
    }

    function showAuditInformationDialog(entity: Entities) {
        setAuditEntity(entity);
        setAuditRowPointer(props.productId);
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function hideMethodLookupComponent() {
        setMethodLookupComponentVisible(false);
    }

    function hideAddTechnologyDialog() {
        setAddTechnologyDialogVisible(false);
    }

    async function getAllData() {
        await Promise.all([
            getProductAnalyticalPlanTechnologies()
        ]);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }
}
